import { memo, useMemo, useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import {
  SelectedItemsMouseSelectionContainer,
  type SelectedItemsMouseSelectionContainerProps,
} from '~/components/SelectedItemsMouseSelectionContainer';
import { TableView, type TableViewProps } from '~/components/TableView/index';
import { type AirTableRow } from '~/components/TableView/types';
import { useTableViewItemsPositions } from '~/components/TableView/useTableViewItemsPositions';
import { useTableView } from './TableViewContext';

export type SelectableTableViewProps = {
  rows: AirTableRow[];
  horizontalPadding: number;
  scrollElementRef: SelectedItemsMouseSelectionContainerProps['scrollElementRef'];
} & Omit<TableViewProps, 'rows' | 'minTableWidth'>;

export const SelectableTableView = memo(
  ({ scrollElementRef, horizontalPadding, ...rest }: SelectableTableViewProps) => {
    const containerRef = useRef(null);
    const { width = 0 } = useResizeDetector({
      targetRef: containerRef,
    });
    const { getTableWidthAsNumber } = useTableView();
    const minTableWidth = useMemo(() => getTableWidthAsNumber(), [getTableWidthAsNumber]);

    const { rowPositions } = useTableViewItemsPositions({
      tableWidth: width,
      rows: rest.rows,
      horizontalPadding,
    });

    return (
      <div className="relative" ref={containerRef}>
        <SelectedItemsMouseSelectionContainer
          containerRef={containerRef}
          itemsPositions={rowPositions}
          scrollElementRef={scrollElementRef}
        >
          <TableView scrollElementRef={scrollElementRef} {...rest} minTableWidth={minTableWidth} />
        </SelectedItemsMouseSelectionContainer>
      </div>
    );
  },
);

SelectableTableView.displayName = 'SelectableTableView';
